import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { assignCard, getCardTypeData, getLinkedCards } from "pages/GenericOrderDetailsPage/apis";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { Spinner } from "components";
import { Check, X } from "lucide-react";

export const tabMapping = {
  activation: "Activation",
  activationReload: "Activation + Reload",
  reload: "Reload"
}

interface AssignPrepaidCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "activation" | "activationReload" | "reload";
  customerId: number | null;
  defaultData?:{
    action_type: string;
    card_number: string;
    card_id: number;
  }
}

const AssignPrepaidCardDialog: React.FC<AssignPrepaidCardDialogProps> = ({
  open,
  onOpenChange,
  defaultTab,
  customerId,
  defaultData
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab || "activation");
  const [selectedCard, setSelectedCard] = useState("");
  const [showLinkCardForm, setShowLinkCardForm] = useState(false);
  const [selectedReloadCard, setSelectedReloadCard] = useState("");
  const [cardNumber, setCardNumber] = useState("");

  const { id } = useParams()
  const queryClient = useQueryClient();

  
  const { data: cardOptions, isLoading: cardOptionsLoading } = useQuery({
    queryKey: ['cardTypes', activeTab],
    queryFn: () => getCardTypeData({ action_type: tabMapping[activeTab] }),
    enabled: !!activeTab
  })
  useEffect(() => {
    if(defaultData) {
      setActiveTab(defaultData.action_type as "activation" | "activationReload" | "reload")
      setCardNumber(defaultData.card_number)
    }
  },[])

  const { data: linkedCards, isLoading: linkedCardsLoading } = useQuery({
    queryKey: ['linkedCards'],
    queryFn: () => getLinkedCards({ customers_id: customerId || 0 }),
    enabled: activeTab === 'reload' && !!customerId,
    staleTime: 1 * 60 * 1000,
  })

  const { mutate, isLoading } = useMutation({
    mutationFn: assignCard,
    onSuccess: () => {
      toast.success("Card assigned successfully");
      queryClient.invalidateQueries(['orderDetails', id]); // Invalidate order details query to refresh data
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to assign card");
    }
  })

  const handleSave = () => {

    mutate({
      action_type: tabMapping[activeTab],
      card_number: cardNumber,
      customers_id: customerId || 0,
      forex_card_id: tabMapping[activeTab] === 'Activation' ? 1 : tabMapping[activeTab] === 'Activation + Reload' ? 2 : 2,
      generic_order_details_id: Number(id),  // Replace with actual order details ID
      customer_forex_card: selectedReloadCard ? Number(selectedReloadCard) : undefined,
    })
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "activation":
        return (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[#999999]">Card Details</h3>
            <div className="space-y-2">
              <Label htmlFor="card-select" className="text-[#333] font-medium text-sm">
                Select Card
              </Label>
              <Select onValueChange={setSelectedCard} value={selectedCard}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a card provider" />
                </SelectTrigger>
                <SelectContent>
                  {cardOptions?.map((card) => (
                    <SelectItem key={card.id} value={card.id.toString()}>
                      {card.card_company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="card-number" className="text-[#333] font-medium text-sm">
                Card Number
              </Label>
              <Input
                id="card-number"
                type="number"
                max={16}
                min={16}
                placeholder="Enter 16 digits card number"
                value={cardNumber}
                onChange={(e) => {
                  if (e.target.value.length <= 16) {
                    setCardNumber(e.target.value);
                  }
                }}
                className="w-full border-[#D2D2D2]"
              />
            </div>
          </div>
        );

      case "activationReload":
        return (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[#999999]">Card Details</h3>
            <div className="space-y-2">
              <Label htmlFor="card-select-reload" className="text-[#333] font-medium text-sm">
                Select Card
              </Label>
              <Select onValueChange={setSelectedCard} value={selectedCard}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a card provider" />
                </SelectTrigger>
                <SelectContent>
                  {cardOptions?.map((card) => (
                    <SelectItem key={card.id} value={card.id.toString()}>
                      {card.card_company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="card-number" className="text-[#333] font-medium text-sm">
                Card Number
              </Label>
              <Input
                id="card-number"
                type="number"
                max={16}
                min={16}
                placeholder="Enter 16 digits card number"
                value={cardNumber}
                onChange={(e) => {
                      if(e.target.value.length <= 16) {
                        setCardNumber(e.target.value);
                      }
                    }}
                className="w-full border-[#D2D2D2]"
              />
            </div>
          </div>
        );

      case "reload":
        return (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[#999] mb-4">Linked Card</h3>

            {!showLinkCardForm ? (
              <div className="">
                {linkedCardsLoading ? (
                  <span className="">Fetching linked cards...</span>
                ) : (
                  <div className="grid grid-cols-2 gap-4 max-h-[200px] overflow-y-auto">
                    {linkedCards?.length ? (
                      linkedCards.map((card) => (
                        <div
                          role='button'
                          key={card.id}
                          className={`p-4 relative rounded-[12px] mb-3 cursor-pointer border border-solid max-w-[240px] ${
                            selectedReloadCard === card.id.toString() 
                              ? 'bg-blue-100 border-blue-700 border-[3px]' 
                              : 'border-[#165FE380]'
                          }`}
                          style={{
                            background: 'linear-gradient(103.13deg, #E1EBFC 7.79%, rgba(225, 235, 252, 0.3) 96.85%)'
                          }}
                          onClick={() => {
                            if(selectedReloadCard === card.id.toString()) {
                              setSelectedReloadCard('');
                              return;
                            }
                            setSelectedReloadCard(card.id.toString());                          
                          }}
                        >
                          <div className="flex flex-col">
                            <p className="font-semibold text-base text-[#333333] leading-[16px] mb-1">
                              {card.card_company} Card
                            </p>
                            <p className="text-[#666666] text-[15px] font-normal mb-2">
                              (multi-currency)
                            </p>
                            <p className="text-[15px] text-[#666666] font-medium">
                              No: {card.card_number.slice(0, 4)}-{card.card_number.slice(4, 8)}-{card.card_number.slice(8, 12)}-{card.card_number.slice(12, 16)}
                            </p>
                          </div>
                          {
                            selectedReloadCard === card.id.toString() && 
                            <div className="w-5 h-5 bg-blue-700 rounded-full absolute bottom-3 right-3 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white-A700 " />
                            </div>
                          }
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500">
                        No linked cards found for this customer.
                      </div>
                    )}
                  </div>
                )
                }
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="card-select-link" className="text-[#333] font-medium text-sm">
                    Select Card
                  </Label>
                  <Select onValueChange={setSelectedCard} value={selectedCard}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a card provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {cardOptions?.map((card) => (
                        <SelectItem key={card.id} value={card.id.toString()}>
                          {card.card_company}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="card-number-link" className="text-[#333] font-medium text-sm">
                    Card Number
                  </Label>
                  <Input
                    id="card-number-link"
                    type="number"                    
                    placeholder="Enter 16 digits card number"
                    value={cardNumber}
                    onChange={(e) => {
                      if(e.target.value.length <= 16) {
                        setCardNumber(e.target.value);
                      }
                    }}
                    className="w-full border-[#D2D2D2]"
                  />
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPortal>
        {isLoading && <Spinner />}
        <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
        <AlertDialogContent className="max-w-[600px] p-0 bg-white-A700 rounded-lg items-start">
          <AlertDialogHeader className="px-10 py-5 border-b items-start space-y-0 relative">
            <div>
              <AlertDialogTitle className="text-lg font-semibold">
                Assign prepaid card
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-gray-500 mt-0">
                Fill the required details
              </AlertDialogDescription>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="absolute right-5 top-5 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </AlertDialogHeader>

          {/* Tabs */}
          <div className="">
            <div className="flex gap-3 px-10 ">
              {[
                { key: "activation", label: "Activation" },
                { key: "activationReload", label: "Activation + Reload" },
                { key: "reload", label: "Reload" },
              ].map((tab) => (
                <Button
                  key={tab.key}
                  variant="ghost"
                  onClick={() =>
                    setActiveTab(
                      tab.key as "activation" | "activationReload" | "reload"
                    )
                  }
                  className={`px-4 py-2 rounded-[8px] text-base font-normal border h-auto ${
                    activeTab === tab.key
                      ? "bg-[#E1EBFC] border-[#E1EBFC] text-[#165FE3] hover:bg-[#E1EBFC]"
                      : "bg-[#F9F9F9] border-[#F9F9F9] text-[#666] hover:bg-[#F9F9F9]"
                  }`}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
            <div className="border-b px-10 pt-3 right-0 relative mx-10 w-64 border-dashed" />
            {/* Content */}
            <div className="px-10 py-5 min-h-[160px]">{renderTabContent()}</div>
          </div>

          <AlertDialogFooter className="border-t flex gap-3 flex-row items-center px-10 py-4 bg-gray-50 rounded-b-xl">
            <AlertDialogCancel className="px-4 py-2 m-0 text-blue-600 border border-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSave}
              disabled={
                activeTab === "reload"
                  ? !selectedReloadCard
                  : !selectedCard || !cardNumber || cardNumber.length !== 16
              }
              className="px-4 py-2 bg-blue-700 text-white-A700 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
};

export default AssignPrepaidCardDialog;
